/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value */
import type { CommonModalProps } from '@frontend/web-react';
import { Anchor, Modal, Text, XStack, YStack } from '@frontend/web-react';
import { useId } from 'react';

import type { PlaceDetails } from './placesApi';

const PriceLevelDisplay = ({ level }: { level: number }) => (
  <XStack gap="none">
    <Text color="secondary" type="p-sm">
      {'$'.repeat(level)}
    </Text>
    <Text color="tertiary" type="p-sm">
      {'$'.repeat(4 - level)}
    </Text>
  </XStack>
);

const StarRating = ({ rating, totalRatings }: { rating: number; totalRatings?: number }) => {
  const gradientId = useId();

  return (
    <XStack alignItems="center" gap="xs">
      <Text type="p-sm">{rating.toFixed(1)}</Text>
      <svg height="0" width="0">
        <defs>
          <linearGradient id={gradientId}>
            <stop offset={`${((rating % 1) * 100).toFixed(0)}%`} stopColor="#facc15" />
            <stop offset={`${((rating % 1) * 100).toFixed(0)}%`} stopColor="#d4d4d8" />
          </linearGradient>
        </defs>
      </svg>
      <XStack gap="none">
        {Array.from({ length: 5 }, (_, i) => {
          const filled = rating - i;
          return (
            <svg
              fill={filled >= 1 ? '#facc15' : filled >= 0.5 ? `url(#${gradientId})` : '#d4d4d8'}
              height="14"
              key={i}
              viewBox="0 0 20 20"
              width="14"
            >
              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
            </svg>
          );
        })}
      </XStack>
      {totalRatings ? (
        <Text color="tertiary" type="caption">
          ({totalRatings.toLocaleString()})
        </Text>
      ) : null}
    </XStack>
  );
};

export const PlaceDetailModal = ({
  details,
  isLoading,
  isOpen,
  onClose,
}: CommonModalProps & { details: PlaceDetails | null; isLoading: boolean }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="560px" title={details?.name ?? 'Location Details'}>
    <YStack className="p-md" gap="md">
      {isLoading ? (
        <YStack alignItems="center" className="py-xxl">
          <Text color="tertiary" type="p-sm">
            Loading place details...
          </Text>
        </YStack>
      ) : details ? (
        <>
          {details.photoUrls.length > 0 ? (
            <XStack className="overflow-x-auto pb-2" gap="xs">
              {details.photoUrls.map((url) => (
                <img alt={details.name} className="h-48 min-w-[240px] rounded-lg object-cover" key={url} src={url} />
              ))}
            </XStack>
          ) : null}

          <YStack gap="sm">
            {details.rating ? <StarRating rating={details.rating} totalRatings={details.totalRatings} /> : null}

            <XStack alignItems="center" gap="md">
              {details.priceLevel != null ? <PriceLevelDisplay level={details.priceLevel} /> : null}
              {details.openNow !== undefined ? (
                <Text color={details.openNow ? 'success' : 'danger'} type="p-sm">
                  {details.openNow ? 'Open now' : 'Closed'}
                </Text>
              ) : null}
            </XStack>

            {details.address ? (
              <XStack alignItems="start" gap="xs">
                <Text className="shrink-0" color="tertiary" type="p-sm">
                  Address:
                </Text>
                <Text type="p-sm">{details.address}</Text>
              </XStack>
            ) : null}

            {details.phoneNumber ? (
              <XStack alignItems="center" gap="xs">
                <Text className="shrink-0" color="tertiary" type="p-sm">
                  Phone:
                </Text>
                <Anchor href={`tel:${details.phoneNumber}`} newTab={false} type="p-sm" withUnderline>
                  {details.phoneNumber}
                </Anchor>
              </XStack>
            ) : null}

            {details.website ? (
              <XStack alignItems="center" gap="xs">
                <Text className="shrink-0" color="tertiary" type="p-sm">
                  Website:
                </Text>
                <Anchor href={details.website} type="p-sm" withUnderline>
                  {(() => {
                    try {
                      return new URL(details.website).hostname;
                    } catch {
                      return details.website;
                    }
                  })()}
                </Anchor>
              </XStack>
            ) : null}
          </YStack>
        </>
      ) : (
        <YStack alignItems="center" className="py-xxl">
          <Text color="tertiary" type="p-sm">
            No details available for this location.
          </Text>
        </YStack>
      )}
    </YStack>
  </Modal>
);
